import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { Button, Input, Label } from "reactstrap";
import {
  ApprovalShow,
  OrderAdvancePayment,
  StudioApproval,
  createOrderApproval,
  getOrderApproval,
  keepOrderApprovalAfterChange,
  logOrderApprovalReminder,
  markOrderAdvance,
  revokeOrderApproval,
} from "../../../services/orderApprovalApi";
import { approvalLabel, approvalTone, changedFieldsText, isStudioStaff } from "./approvalStatus";

/**
 * refreshKey changes whenever the order is saved, so a link the server just superseded or revoked never shows as live.
 * role: the signed-in user's role; only studio staff see the panel (the API answers 403 to anyone else).
 */
/** An order file the studio may share on the link (the order view's documents, audio notes left out). */
export type ShareableDoc = { id?: string; url: string; fileName?: string; contentType?: string };

type Props = {
  orderId: string;
  canWrite: boolean;
  customerPhone?: string;
  refreshKey?: string;
  role?: string | null;
  /** Saved order files the customer may be shown; ticked ones go on the link. */
  documents?: ShareableDoc[];
  /** The order's advance from the order view; the panel records it as received or undoes that. */
  advancePayment?: OrderAdvancePayment | null;
  /** Called with the order view's advance after a change, so the order screen stays in step. */
  onAdvanceChange?: (advance: OrderAdvancePayment | null) => void;
};

/** The id the server knows a file by: its id when the order view gives one, else its stored URL. */
export const docKey = (doc: ShareableDoc): string => doc.id || doc.url;

const todayIst = (): string => moment().utcOffset(330).format("YYYY-MM-DD");

const ADVANCE_LABEL: Record<OrderAdvancePayment["status"], string> = {
  DUE: "Advance due",
  CUSTOMER_SAYS_PAID: "Customer says paid",
  RECEIVED: "Advance received",
};
const ADVANCE_TONE: Record<OrderAdvancePayment["status"], string> = { DUE: "orange", CUSTOMER_SAYS_PAID: "blue", RECEIVED: "green" };

const rupees = (n?: number | null) => (n == null ? "" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

const SHOW_LABELS: Array<[keyof ApprovalShow, string]> = [
  ["price", "Price"],
  ["advance", "Advance"],
  ["images", "Photos"],
  ["specs", "Details"],
];

/** A fresh key per create attempt; crypto.randomUUID is missing on older phones and in tests. */
const newKey = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/** The customer's answer on updates: a tick is a yes, "don't send" a no, nothing ticked no answer (updates continue). */
const consentText = (c: { whatsapp?: boolean | null; email?: boolean | null }) => {
  const yes = [c.whatsapp === true ? "WhatsApp" : "", c.email === true ? "email" : ""].filter(Boolean);
  if (yes.length) return `Customer agreed to updates on ${yes.join(" and ")}`;
  if (c.whatsapp === false && c.email === false) return "Customer asked not to get updates";
  return "Customer didn't answer about updates (they keep getting them)";
};

const when = (iso?: string) => (iso ? moment(new Date(iso)).format("D MMM YYYY, HH:mm") : "");

/** WhatsApp chat link with the approval URL; the number is the customer's from the order. */
export const whatsappShare = (url: string, phone?: string): string => {
  const digits = (phone || "").replace(/\D/g, "").replace(/^0+(?=\d{10}$)/, "");
  const to = digits.length === 10 ? `91${digits}` : digits.length > 10 ? digits : "";
  const message = `Please check your order details and approve: ${url}`;
  return `https://wa.me/${to}?text=${encodeURIComponent(message)}`;
};

/**
 * The order's customer approval link: make one, copy or send it, see views and the decision, log a
 * reminder or stop an unanswered link. A customer's answer is final: when the order changes after it,
 * the panel lists the changes and the studio sends it for approval again or keeps the approval.
 * Hidden when approval links are off for this environment.
 */
const OrderApprovalPanel = ({
  orderId,
  canWrite,
  customerPhone,
  refreshKey,
  role,
  documents = [],
  advancePayment,
  onAdvanceChange,
}: Props) => {
  const staff = isStudioStaff(role);
  const [approval, setApproval] = useState<StudioApproval | null>(null);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [making, setMaking] = useState(false);
  const [show, setShow] = useState<ApprovalShow>({ price: true, advance: true, images: true, specs: true });
  const [language, setLanguage] = useState<"en" | "hi">("en");
  const [title, setTitle] = useState("");
  const [copied, setCopied] = useState(false);
  // Order files to share on the next link; none by default, so nothing internal goes out by accident.
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceOn, setAdvanceOn] = useState(todayIst());
  const [advanceNote, setAdvanceNote] = useState("");
  const [advanceBusy, setAdvanceBusy] = useState(false);
  const [advanceError, setAdvanceError] = useState("");
  // Warnings from "Make link" (e.g. the phone number on the page); kept until the next action, since the reload after it has none.
  const [createWarnings, setCreateWarnings] = useState<string[]>([]);
  // Only the newest load may write state; an older answer arriving late is dropped.
  const loadSeq = useRef(0);
  // One key per create attempt: a double tap or a retry after a lost answer reuses it. Cleared once a link is made.
  const createKey = useRef<string | null>(null);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError("");
    try {
      const res: any = await getOrderApproval(orderId);
      if (seq !== loadSeq.current) return;
      if (res?.success) {
        setApproval(res.data);
      } else if (res?.code === "FEATURE_DISABLED" || res?.code === "PERMISSION_DENIED" || res?.status === 404) {
        setHidden(true);
      } else {
        setError(res?.message || "Couldn't load the approval link");
      }
    } catch (e: any) {
      if (seq !== loadSeq.current) return;
      if (e?.response?.status === 404) setHidden(true);
      else setError("Couldn't load the approval link");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (staff) load();
  }, [load, refreshKey, staff]);

  const act = async (run: () => Promise<any>, keepWarnings = false) => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setCreateWarnings([]);
    try {
      const res: any = await run();
      if (res?.success) {
        setApproval(res.data as StudioApproval);
        if (keepWarnings) setCreateWarnings((res.data as StudioApproval)?.warnings || []);
        // Reload so history and the order's other links are current.
        load();
        return true;
      }
      setError(res?.message || "Couldn't complete that");
    } catch (e: any) {
      setError(e?.response?.data?.message || "Couldn't complete that");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
    return false;
  };

  const make = async () => {
    if (!createKey.current) createKey.current = newKey();
    const idempotencyKey = createKey.current;
    const ok = await act(
      () =>
        createOrderApproval(orderId, {
          show,
          language,
          customerTitle: title || undefined,
          idempotencyKey,
          fileIds: fileIds.filter((id) => documents.some((d) => docKey(d) === id)),
        }),
      true
    );
    if (ok) {
      createKey.current = null;
      setMaking(false);
    }
  };

  const revoke = async () => {
    if (!approval?.approvalId) return;
    const reason = window.prompt("Why stop this link? (the customer will see it is no longer active)");
    if (!reason || !reason.trim()) return;
    await act(() => revokeOrderApproval(approval.approvalId as string, reason.trim()));
  };

  const keep = async () => {
    if (!approval?.approvalId) return;
    const note = window.prompt("Keep the customer's approval without asking again? Add a short reason (optional).", "");
    if (note === null) return;
    await act(() => keepOrderApprovalAfterChange(approval.approvalId as string, note.trim() || undefined));
  };

  const remind = async (channel: string) => {
    if (!approval?.approvalId) return;
    await act(() => logOrderApprovalReminder(approval.approvalId as string, channel));
  };

  const saveAdvance = async (received: boolean) => {
    if (advanceBusy) return;
    let amount: number | undefined;
    if (received && advanceAmount.trim()) {
      amount = Number(advanceAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        setAdvanceError("Enter the amount received, or leave it empty");
        return;
      }
    }
    if (!received && !window.confirm("Undo the advance received? The customer will see it as due again.")) return;
    setAdvanceBusy(true);
    setAdvanceError("");
    try {
      const res: any = await markOrderAdvance(
        orderId,
        received ? { received, amount, receivedOn: advanceOn || undefined, note: advanceNote.trim() || undefined } : { received }
      );
      if (res?.success) {
        onAdvanceChange?.(res.data?.advancePayment || null);
        setAdvanceOpen(false);
        setAdvanceNote("");
      } else {
        setAdvanceError(res?.message || "Couldn't save the advance");
      }
    } catch (e: any) {
      setAdvanceError(e?.response?.data?.message || "Couldn't save the advance");
    } finally {
      setAdvanceBusy(false);
    }
  };

  const openAdvance = () => {
    setAdvanceAmount(advancePayment?.amount != null ? String(advancePayment.amount) : "");
    setAdvanceOn(todayIst());
    setAdvanceNote("");
    setAdvanceError("");
    setAdvanceOpen(true);
  };

  const copy = async () => {
    if (!approval?.url) return;
    try {
      await navigator.clipboard.writeText(approval.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      window.prompt("Copy the link", approval.url);
    }
  };

  if (!staff || hidden) return null;

  const status = approval?.status || null;
  const live = status === "PENDING";
  const decided = status === "APPROVED" || status === "CHANGES_REQUESTED";
  const changes = decided ? approval?.changesAfterDecision || [] : [];
  const needsDecision = !!approval?.needsReapprovalDecision;

  return (
    <div className="geo-card order-approval-panel">
      <div className="order-approval-panel__head">
        <Label className="mb-0">Customer approval</Label>
        {status ? <span className={`order-approval-chip is-${approvalTone(status)}`}>{approvalLabel(status)}</span> : null}
      </div>
      {loading ? <p className="gi-au-hint">Loading…</p> : null}
      {error ? (
        <p className="gi-form-banner is-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !status && !making ? (
        <p className="gi-au-hint">
          Send the customer a private link to check this order and approve it, or ask for a change.
        </p>
      ) : null}

      {!loading && status ? (
        <ul className="order-approval-facts">
          {live && approval?.expiresAt ? <li>Open until {when(approval.expiresAt)}</li> : null}
          <li>
            {approval?.views ? `Opened ${approval.views} time${approval.views === 1 ? "" : "s"}, last ${when(approval.lastViewedAt)}` : "Not opened yet"}
          </li>
          {approval?.decidedAt ? (
            <li>
              {status === "APPROVED" ? "Approved" : "Change asked"} by {approval.decidedByName || "customer"} on {when(approval.decidedAt)}
            </li>
          ) : null}
          {approval?.decisionNote ? <li className="order-approval-facts__note">“{approval.decisionNote}”</li> : null}
          {approval?.files && approval.files.length > 0 ? (
            <li>Documents shared: {approval.files.map((f) => f.name || "File").join(", ")}</li>
          ) : null}
          {approval?.consent ? (
            <li>
              {consentText(approval.consent)}
              {approval.consent.at ? `, ${when(approval.consent.at)}` : ""}
            </li>
          ) : null}
          {changes.map((c, i) => (
            <li key={`${c.at}-${i}`} className="order-approval-facts__change">
              {c.kind === "CANCELLED" ? "Order cancelled" : c.kind === "REMOVED" ? "Order removed" : "Order changed"}
              {c.kind === "EDITED" && c.fields && c.fields.length > 0 ? ` (${changedFieldsText(c.fields)})` : ""} after the
              customer's answer, {when(c.at)}
              {c.byName ? ` by ${c.byName}` : ""}
            </li>
          ))}
          {approval?.reapproval && !needsDecision && changes.length > 0 ? (
            <li>
              Kept without asking again by {approval.reapproval.byName || "the studio"} on {when(approval.reapproval.at)}
              {approval.reapproval.note ? ` (“${approval.reapproval.note}”)` : ""}
            </li>
          ) : null}
          {approval?.reminders && approval.reminders.length > 0 ? (
            <li>
              Reminders: {approval.reminders.length} (last {approval.reminders[approval.reminders.length - 1].channel},{" "}
              {when(approval.reminders[approval.reminders.length - 1].at)})
            </li>
          ) : null}
          {Array.from(new Set([...(approval?.warnings || []), ...createWarnings])).map((w) => (
            <li key={w} className="order-approval-facts__warn">
              {w}
            </li>
          ))}
        </ul>
      ) : null}

      {!loading && approval?.history && approval.history.length > 0 ? (
        <details className="order-approval-history">
          <summary>Earlier links ({approval.history.length})</summary>
          <ul className="order-approval-facts">
            {approval.history.map((h) => {
              const answered = h.status === "APPROVED" || h.status === "CHANGES_REQUESTED";
              return (
                <li key={h.approvalId}>
                  {answered
                    ? `${h.status === "APPROVED" ? "Approved" : "Change asked"} by ${h.decidedByName || "customer"} on ${when(h.decidedAt)}`
                    : `${approvalLabel(h.status)}, made ${when(h.createdAt)}`}
                  {h.decisionNote ? ` (“${h.decisionNote}”)` : ""}
                  {(h.changesAfterDecision || []).map((c, i) => (
                    <div key={`${c.at}-${i}`} className="order-approval-facts__change">
                      {c.kind === "CANCELLED" ? "Order cancelled" : c.kind === "REMOVED" ? "Order removed" : "Order changed"}
                      {c.kind === "EDITED" && c.fields && c.fields.length > 0 ? ` (${changedFieldsText(c.fields)})` : ""}, {when(c.at)}
                      {c.byName ? ` by ${c.byName}` : ""}
                    </div>
                  ))}
                  {h.reapproval ? (
                    <div>
                      Kept without asking again by {h.reapproval.byName || "the studio"} on {when(h.reapproval.at)}
                      {h.reapproval.note ? ` (“${h.reapproval.note}”)` : ""}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}

      {needsDecision && canWrite && !making ? (
        <div className="gi-form-banner is-info order-approval-changed" role="status">
          <p className="mb-1">The order changed after the customer approved it. Their approval stays on record.</p>
          <p className="mb-2">Send it for approval again, or keep the approval if this change doesn't need it.</p>
          <div className="gi-form-actions">
            <Button
              className="ResetBtn"
              disabled={busy}
              onClick={() => {
                createKey.current = null;
                setMaking(true);
              }}
            >
              Send for approval again
            </Button>
            <Button className="btn-hastkala-outline" disabled={busy} onClick={keep}>
              No new approval needed
            </Button>
          </div>
        </div>
      ) : null}

      {live && approval?.url ? (
        <div className="order-approval-link">
          <Input readOnly value={approval.url} aria-label="Approval link" onFocus={(e) => e.target.select()} />
          <div className="gi-form-actions">
            <Button className="btn-hastkala-outline" onClick={copy}>
              {copied ? "Copied" : "Copy link"}
            </Button>
            <a
              className="btn ResetBtn"
              href={whatsappShare(approval.url, customerPhone)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Send on WhatsApp
            </a>
          </div>
        </div>
      ) : null}

      {canWrite && making ? (
        <div className="order-approval-make">
          <p className="gi-au-hint mb-1">Show the customer</p>
          <div className="order-approval-show">
            {SHOW_LABELS.map(([key, label]) => (
              <label key={key}>
                <input type="checkbox" checked={show[key]} onChange={(e) => setShow((s) => ({ ...s, [key]: e.target.checked }))} />{" "}
                {label}
              </label>
            ))}
          </div>
          <fieldset className="order-approval-docs">
            <legend className="gi-au-hint mb-1">Documents the customer can open</legend>
            {documents.length > 0 ? (
              documents.map((doc) => {
                const key = docKey(doc);
                return (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={fileIds.includes(key)}
                      onChange={(e) =>
                        setFileIds((ids) => (e.target.checked ? [...ids, key] : ids.filter((id) => id !== key)))
                      }
                    />{" "}
                    <span>{doc.fileName || "Attached file"}</span>
                  </label>
                );
              })
            ) : (
              <p className="gi-au-hint mb-0">No saved documents on this order. Attach and save them to share.</p>
            )}
          </fieldset>
          <div className="order-approval-row">
            <Label for="oaLang" className="mb-0">
              Page language
            </Label>
            <Input id="oaLang" type="select" value={language} onChange={(e) => setLanguage(e.target.value as "en" | "hi")}>
              <option value="en">English</option>
              <option value="hi">हिंदी</option>
            </Input>
          </div>
          <div className="order-approval-row">
            <Label for="oaTitle" className="mb-0">
              After the name
            </Label>
            <Input id="oaTitle" type="select" value={title} onChange={(e) => setTitle(e.target.value)}>
              <option value="">Nothing</option>
              <option value="Ma'am">Ma'am</option>
              <option value="Sir">Sir</option>
            </Input>
          </div>
          <div className="gi-form-actions">
            <Button className="btn-hastkala-outline" disabled={busy} onClick={() => setMaking(false)}>
              Cancel
            </Button>
            <Button className="ResetBtn" disabled={busy} onClick={make}>
              {busy ? "Making…" : status ? "Make new link" : "Make link"}
            </Button>
          </div>
          <p className="gi-au-hint mb-0">Uses the last saved version of this order.</p>
          {live ? (
            <p className="gi-au-hint mb-0">A new link replaces the current one; the old link will say a newer version was sent.</p>
          ) : decided ? (
            <p className="gi-au-hint mb-0">The customer's earlier answer stays on record.</p>
          ) : null}
        </div>
      ) : null}

      {canWrite && !making && !loading && !needsDecision ? (
        <div className="gi-form-actions">
          <Button
            className={status ? "btn-hastkala-outline" : "ResetBtn"}
            disabled={busy}
            onClick={() => {
              createKey.current = null;
              setMaking(true);
            }}
          >
            {status ? "New link" : "Make approval link"}
          </Button>
          {live ? (
            <Input
              type="select"
              aria-label="Log a reminder"
              className="order-approval-remind"
              value=""
              disabled={busy}
              onChange={(e) => e.target.value && remind(e.target.value)}
            >
              <option value="">Log a reminder…</option>
              <option value="whatsapp">Reminded on WhatsApp</option>
              <option value="call">Reminded by call</option>
              <option value="instagram">Reminded on Instagram</option>
            </Input>
          ) : null}
          {live ? (
            <Button className="btn-hastkala-outline" disabled={busy} onClick={revoke}>
              Stop link
            </Button>
          ) : null}
        </div>
      ) : null}

      {!loading ? (
        <div className="order-approval-advance">
          <div className="order-approval-panel__head">
            <span className="gi-au-hint mb-0">Advance</span>
            <span className={`order-approval-chip is-${advancePayment?.status ? ADVANCE_TONE[advancePayment.status] : "grey"}`}>
              {advancePayment?.status ? ADVANCE_LABEL[advancePayment.status] : "Not recorded"}
            </span>
          </div>
          <ul className="order-approval-facts">
            {advancePayment?.status === "RECEIVED" ? (
              <li>
                {advancePayment.amount != null ? `${rupees(advancePayment.amount)} received` : "Received"}
                {advancePayment.receivedAt ? ` on ${moment(advancePayment.receivedAt).format("D MMM YYYY")}` : ""}
                {advancePayment.receivedByName ? `, marked by ${advancePayment.receivedByName}` : ""}
                {advancePayment.note ? ` (“${advancePayment.note}”)` : ""}
              </li>
            ) : advancePayment?.amount != null ? (
              <li>{rupees(advancePayment.amount)} to collect</li>
            ) : null}
            {advancePayment?.customerPaidAt ? (
              <li>
                Customer said they paid on {when(advancePayment.customerPaidAt)}
                {advancePayment.customerReference ? `, reference ${advancePayment.customerReference}` : ", no reference given"}
              </li>
            ) : null}
          </ul>
          {advanceError ? (
            <p className="gi-form-banner is-error" role="alert">
              {advanceError}
            </p>
          ) : null}
          {canWrite && advanceOpen ? (
            <div className="order-approval-make">
              <div className="order-approval-row">
                <Label for="oaAdvAmount" className="mb-0">
                  Amount (₹)
                </Label>
                <Input
                  id="oaAdvAmount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(e.target.value)}
                />
              </div>
              <div className="order-approval-row">
                <Label for="oaAdvOn" className="mb-0">
                  Received on
                </Label>
                <Input id="oaAdvOn" type="date" value={advanceOn} max={todayIst()} onChange={(e) => setAdvanceOn(e.target.value)} />
              </div>
              <div className="order-approval-row">
                <Label for="oaAdvNote" className="mb-0">
                  Note
                </Label>
                <Input
                  id="oaAdvNote"
                  value={advanceNote}
                  maxLength={200}
                  placeholder="UPI ref, cash, bank transfer…"
                  onChange={(e) => setAdvanceNote(e.target.value)}
                />
              </div>
              <div className="gi-form-actions">
                <Button className="btn-hastkala-outline" disabled={advanceBusy} onClick={() => setAdvanceOpen(false)}>
                  Cancel
                </Button>
                <Button className="ResetBtn" disabled={advanceBusy} onClick={() => saveAdvance(true)}>
                  {advanceBusy ? "Saving…" : "Save as received"}
                </Button>
              </div>
            </div>
          ) : null}
          {canWrite && !advanceOpen ? (
            <div className="gi-form-actions">
              {advancePayment?.status === "RECEIVED" ? (
                <Button className="btn-hastkala-outline" disabled={advanceBusy} onClick={() => saveAdvance(false)}>
                  {advanceBusy ? "Saving…" : "Undo"}
                </Button>
              ) : (
                <Button className="btn-hastkala-outline" disabled={advanceBusy} onClick={openAdvance}>
                  Mark advance received
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default OrderApprovalPanel;
